// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// NexoraX Voice Command Manager (Node-09.2)
// Custom Voice Command Mapping Hub, Live Spectrum Waveform, and Executable Operations Tracker
// Intellectual Property Status: Patent-Readiness Protected Assets, © NexoraX Digital Ltd (SC887095)

import React, { useState, useEffect, useRef } from "react";
import { 
  Mic, MicOff, Play, PlayCircle, Settings, Shield, Activity, 
  BookOpen, HelpCircle, ChevronRight, CheckCircle2, AlertTriangle, 
  Clock, Terminal, Database, FileText, Send, UserCheck, ShieldAlert,
  Plus, Trash2, Zap, Search, Edit, Layers
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

export interface VoiceIntent {
  id: string;
  actionName: string;
  intents: string[];
  category: "Security & Vault" | "Infrastructure" | "Intelligence & Reports" | "Support & Assistance";
  description: string;
  systemAction: string;
  voiceFeedback: string;
}

export const REGISTERED_VOICE_COMMANDS: VoiceIntent[] = [
  {
    id: "open-vault",
    actionName: "open vault",
    intents: ["open vault", "activate vault", "unlock vault", "access vault", "NexoraX Resource Library"],
    category: "Security & Vault",
    description: "NexoraX secure file decryption for the Google Drive NexoraX Resource Library via CloudSync-02.",
    systemAction: "ACTIVATE_VAULT",
    voiceFeedback: "NexoraX Resource Library level cleared. Mirroring current background source synchronizations."
  },
  {
    id: "scan-infra",
    actionName: "scan infrastructure",
    intents: ["scan infrastructure", "scan nodes", "check infrastructure", "infrastructure integrity"],
    category: "Infrastructure",
    description: "Launches real-time integrity scans across all 18 active sector grids.",
    systemAction: "SCAN_INFRASTRUCTURE",
    voiceFeedback: "Scanning active sector grids. Node 01, 06, 16, and 17 return healthy telemetry indices."
  },
  {
    id: "gen-report",
    actionName: "generate report",
    intents: ["generate report", "create revenue report", "build ledger report", "compile harvest"],
    category: "Intelligence & Reports",
    description: "Generates current daily gross harvest and multi-stage financial calculations report.",
    systemAction: "GENERATE_REPORT",
    voiceFeedback: "Financial extraction report compiled. Net settlements routed to trust ledger sequence."
  },
  {
    id: "sys-status",
    actionName: "system status",
    intents: ["system status", "check nodes", "how is nexorax doing", "heartbeat state"],
    category: "Infrastructure",
    description: "Displays current operational latency index, Planet Telecom pipelines, and online nodes.",
    systemAction: "CHECK_REGISTRY",
    voiceFeedback: "NexoraX Core OS reporting perfect execution velocity at 0.8 seconds latency."
  },
  {
    id: "kd-base",
    actionName: "knowledge base",
    intents: ["knowledge base", "open documentation", "access knowledge", "architect knowledge"],
    category: "Support & Assistance",
    description: "Provides structured technical instructions and guidelines on the NexoraX system framework.",
    systemAction: "SHOW_KNOWLEDGE",
    voiceFeedback: "Deploying NexoraX Knowledge Base panel for technical systems architectures."
  },
  {
    id: "cust-support",
    actionName: "customer support",
    intents: ["customer support", "support channel", "contact support", "administrative support"],
    category: "Support & Assistance",
    description: "Directs target inquiries to support@nexoraxdigital.co.uk and lists hotlines.",
    systemAction: "TRIGGER_SUPPORT",
    voiceFeedback: "Directing technical support queries to our Enterprise Mail Core and the 08000 freephone frequency."
  },
  {
    id: "inbound-qa",
    actionName: "inbound Q&A",
    intents: ["inbound q and a", "ask questions", "faq node", "frequently asked questions"],
    category: "Support & Assistance",
    description: "Activates predictive Q&A responder tracking prospective client inquiries.",
    systemAction: "SHOW_FAQ",
    voiceFeedback: "Predictive Q&A responder online. Awaiting inbound information request."
  },
  {
    id: "inout-support",
    actionName: "inbound & outbound support",
    intents: ["inbound and outbound support", "full support routing", "unified support link"],
    category: "Support & Assistance",
    description: "Syncs general mandates with remote nodes via 'Pakistan_Node_Sync' protocol.",
    systemAction: "SYNC_SUPPORT_UNIFIED",
    voiceFeedback: "Initiating Pakistan Node Synchronization. General support lines synchronized."
  },
  {
    id: "help-login-join",
    actionName: "help on login & join",
    intents: ["help on login and join", "sign up help", "authentication help", "mfa support", "how to join"],
    category: "Support & Assistance",
    description: "Guides user through initial email/password verification and multi-stage login setup.",
    systemAction: "HELP_AUTH",
    voiceFeedback: "Activating step-by-step guidance for authentication credentialing."
  }
];

export const VoiceCommandManager: React.FC = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [textToProcess, setTextToProcess] = useState("");
  const [audioFeedbackText, setAudioFeedbackText] = useState("Awaiting voice command input stream...");
  const [commandLogs, setCommandLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] Node-09.2 autonomous engine online.`,
    `[${new Date().toLocaleTimeString()}] Secure mirroring channel mapped with CloudSync-02.`
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  // Quick Actions Macros definitions
  interface MacroStep {
    id: string;
    type: "OPEN_TAB" | "SET_OPACITY" | "NOTIFY_SUPPORT" | "TOGGLE_ANC_BOOST" | "SIMULATE_REVENUE_INFLOW" | "TRIGGER_GAVEL_ALARM" | "CALCULATE_NEXORAX_TAX";
    paramString?: string;
    landingTabUrl?: string; // Sub-tab landing context
    paramNumber?: number; // Opacity 0.1 - 1.0
  }

  interface QuickActionMacro {
    id: string;
    customPhrase: string; // spoken intent trigger (e.g., "activate matrix")
    targetTab: string; // main tab name (e.g., "NODE 0: ARCHITECT TERMINAL", "REALITY AUDIT LEDGER", "NEXORAX WORKPLACE", etc.)
    landingTab: string | null; // landing view tab (e.g., "Home", "Login", "Join", "Team")
    macroAction: "NONE" | "TOGGLE_ANC_BOOST" | "SIMULATE_REVENUE_INFLOW" | "TRIGGER_GAVEL_ALARM" | "CALCULATE_NEXORAX_TAX" | "COMPLEX_SEQUENCE";
    voiceResponse: string; // spoken audio feedback e.g., "Custom macro active. Navigating core matrix."
    sequenceSteps?: MacroStep[];
  }

  const [quickActions, setQuickActions] = useState<QuickActionMacro[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_vcm_quick_actions");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse quick actions", e);
        }
      }
      return [
        {
          id: "qa-1",
          customPhrase: "launch tactical layout",
          targetTab: "NODE 0: ARCHITECT TERMINAL",
          landingTab: "Home",
          macroAction: "NONE",
          voiceResponse: "Tactical configuration active and running."
        },
        {
          id: "qa-2",
          customPhrase: "trigger revenue pulse",
          targetTab: "REALITY AUDIT LEDGER",
          landingTab: null,
          macroAction: "SIMULATE_REVENUE_INFLOW",
          voiceResponse: "Corporate revenue pulse triggered. Injecting simulated gross settlement stream."
        },
        {
          id: "qa-3",
          customPhrase: "system override sequence",
          targetTab: "NODE 0: ARCHITECT TERMINAL",
          landingTab: null,
          macroAction: "COMPLEX_SEQUENCE",
          voiceResponse: "Executing complete systems override sequence now.",
          sequenceSteps: [
            { id: "step-1", type: "OPEN_TAB", paramString: "NODE 0: ARCHITECT TERMINAL", landingTabUrl: "Home" },
            { id: "step-2", type: "SET_OPACITY", paramNumber: 0.5 },
            { id: "step-3", type: "NOTIFY_SUPPORT", paramString: "OVERRIDE ALERT: Systems override initiated by admin." },
            { id: "step-4", type: "TOGGLE_ANC_BOOST" }
          ]
        }
      ];
    }
    return [];
  });

  // Adding quick action form state
  const [newPhrase, setNewPhrase] = useState("");
  const [newTargetTab, setNewTargetTab] = useState("NODE 0: ARCHITECT TERMINAL");
  const [newLandingTab, setNewLandingTab] = useState("");
  const [newMacroAction, setNewMacroAction] = useState<QuickActionMacro["macroAction"]>("NONE");
  const [newVoiceResponse, setNewVoiceResponse] = useState("");
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [macroSearchQuery, setMacroSearchQuery] = useState("");

  // Sequenced editor modal states
  const [vcmOpacity, setVcmOpacity] = useState<number>(1.0);
  const [editingMacro, setEditingMacro] = useState<QuickActionMacro | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [modalPhrase, setModalPhrase] = useState("");
  const [modalResponse, setModalResponse] = useState("");
  const [modalSteps, setModalSteps] = useState<MacroStep[]>([]);

  const openEditorModal = (macro: QuickActionMacro) => {
    setEditingMacro(macro);
    setModalPhrase(macro.customPhrase);
    setModalResponse(macro.voiceResponse);
    setModalSteps(macro.sequenceSteps || [
      { id: "step-" + Date.now(), type: "OPEN_TAB", paramString: "NODE 0: ARCHITECT TERMINAL", landingTabUrl: "Home" }
    ]);
    setShowEditorModal(true);
    addLog(`SEQUENCE EDITOR: Loading sequence steps for [Macro: "${macro.customPhrase}"].`);
  };

  const handleAddModalStep = () => {
    const newStep: MacroStep = {
      id: "step-" + Date.now() + Math.random().toString(36).substr(2, 4),
      type: "OPEN_TAB",
      paramString: "NODE 0: ARCHITECT TERMINAL",
      landingTabUrl: "Home"
    };
    setModalSteps(prev => [...prev, newStep]);
    addLog("SEQUENCE FORM: Expanded macro instruction buffer.");
  };

  const handleDeleteModalStep = (id: string) => {
    setModalSteps(prev => prev.filter(step => step.id !== id));
    addLog("SEQUENCE FORM: Removed step from instruction buffer.");
  };

  const handleMoveStep = (index: number, direction: "UP" | "DOWN") => {
    const steps = [...modalSteps];
    if (direction === "UP" && index > 0) {
      const tmp = steps[index - 1];
      steps[index - 1] = steps[index];
      steps[index] = tmp;
    } else if (direction === "DOWN" && index < steps.length - 1) {
      const tmp = steps[index + 1];
      steps[index + 1] = steps[index];
      steps[index] = tmp;
    }
    setModalSteps(steps);
    addLog(`SEQUENCE FORM: Re-arranged step execution queues.`);
  };

  const handleUpdateStepType = (id: string, type: MacroStep["type"]) => {
    setModalSteps(prev => prev.map(step => {
      if (step.id !== id) return step;
      let paramString = undefined;
      let paramNumber = undefined;
      let landingTabUrl = undefined;
      
      if (type === "OPEN_TAB") {
        paramString = "NODE 0: ARCHITECT TERMINAL";
        landingTabUrl = "Home";
      } else if (type === "SET_OPACITY") {
        paramNumber = 0.5;
      } else if (type === "NOTIFY_SUPPORT") {
        paramString = "Override Warning: systems override triggered by operator.";
      }
      
      return { ...step, type, paramString, paramNumber, landingTabUrl };
    }));
  };

  const handleUpdateStepString = (id: string, value: string) => {
    setModalSteps(prev => prev.map(step => step.id === id ? { ...step, paramString: value } : step));
  };

  const handleUpdateStepLanding = (id: string, value: string) => {
    setModalSteps(prev => prev.map(step => step.id === id ? { ...step, landingTabUrl: value } : step));
  };

  const handleUpdateStepNumber = (id: string, value: number) => {
    setModalSteps(prev => prev.map(step => step.id === id ? { ...step, paramNumber: value } : step));
  };

  const handleSaveSequenceMacro = () => {
    if (!modalPhrase.trim()) {
      alert("Spoken intent trigger phrase cannot be empty.");
      return;
    }
    
    const updatedMacro: QuickActionMacro = {
      id: editingMacro?.id || "qa-" + Date.now(),
      customPhrase: modalPhrase.trim().toLowerCase(),
      targetTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      voiceResponse: modalResponse.trim() || `Actuating sequenced macro for ${modalPhrase}`,
      macroAction: "COMPLEX_SEQUENCE",
      sequenceSteps: modalSteps
    };
    
    let updatedList;
    const exists = quickActions.some(qa => qa.id === editingMacro?.id);
    if (exists) {
      updatedList = quickActions.map(qa => qa.id === editingMacro?.id ? updatedMacro : qa);
    } else {
      updatedList = [updatedMacro, ...quickActions];
    }
    
    saveQuickActionsList(updatedList);
    speakText(`Sequence macro synchronized.`);
    setShowEditorModal(false);
    setEditingMacro(null);
  };

  const saveQuickActionsList = (list: QuickActionMacro[]) => {
    setQuickActions(list);
    localStorage.setItem("nexorax_vcm_quick_actions", JSON.stringify(list));
  };

  // Command History tracking the last 10 recognized voice intents for audit purposes
  interface RecognizedHistoryItem {
    timestamp: string;
    intentSpoken: string;
    matchedAction: string;
    confidence: number;
    category: string;
    status: "GRANTED" | "BLOCKED" | "UNRESOLVED";
  }

  const [recognizedHistory, setRecognizedHistory] = useState<RecognizedHistoryItem[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("nexorax_vcm_history");
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const addHistoryRecord = (
    spoken: string,
    action: string,
    confidence: number,
    status: "GRANTED" | "BLOCKED" | "UNRESOLVED",
    category: string
  ) => {
    const newRecord: RecognizedHistoryItem = {
      timestamp: new Date().toLocaleTimeString() + " " + new Date().toLocaleDateString(),
      intentSpoken: spoken,
      matchedAction: action,
      confidence: confidence,
      category: category,
      status: status
    };
    setRecognizedHistory(prev => {
      const updated = [newRecord, ...prev].slice(0, 10);
      localStorage.setItem("nexorax_vcm_history", JSON.stringify(updated));
      return updated;
    });
  };

  // Voice & System Configuration states
  const [minConfidence, setMinConfidence] = useState(() => {
    if (typeof window !== 'undefined') {
      return Number(localStorage.getItem("nexorax_vcm_confidence") || "0.45");
    }
    return 0.45;
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("nexorax_vcm_language") || "en-GB";
    }
    return "en-GB";
  });
  const [speechRate, setSpeechRate] = useState(() => {
    if (typeof window !== 'undefined') {
      return Number(localStorage.getItem("nexorax_vcm_speech_rate") || "1.05");
    }
    return 1.05;
  });
  const [speechPitch, setSpeechPitch] = useState(() => {
    if (typeof window !== 'undefined') {
      return Number(localStorage.getItem("nexorax_vcm_speech_pitch") || "0.95");
    }
    return 0.95;
  });
  const [ancBoost, setAncBoost] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem("nexorax_vcm_anc_boost") !== "false";
    }
    return true;
  });

  // ==================== VOICE TRAINING MODULES ====================
  interface TrainingPhrase {
    index: number;
    text: string;
    description: string;
    vocalProfileHint: string;
  }

  const TRAINING_PHRASES: TrainingPhrase[] = [
    {
      index: 1,
      text: "nexorax initialize NexoraX core engine",
      description: "Sets base frequency signature for operational command queries.",
      vocalProfileHint: "Speak clearly at standard boardroom amplitude."
    },
    {
      index: 2,
      text: "authorize direct settlement route to Secure Payment Provider",
      description: "Calibrates critical alphanumeric numbers and ledger instructions representation.",
      vocalProfileHint: "Maintain authoritative velocity, emphasizing 'Secure Payment Provider' metrics."
    },
    {
      index: 3,
      text: "mirror NexoraX Resource Library backups using guardian two",
      description: "Maps alphanumeric characters and system paths security keywords.",
      vocalProfileHint: "Speak with enterprise-grade crispness."
    },
    {
      index: 4,
      text: "activate active noise correction algorithms",
      description: "Samples ambient room reflection and vocal harmonic resonance peaks.",
      vocalProfileHint: "Normal posture, flat response tone."
    },
    {
      index: 5,
      text: "trigger immediate tiered extraction protocol audit",
      description: "Calibrates multi-stage extraction calculations thresholds.",
      vocalProfileHint: "High-level professional lead pace."
    },
    {
      index: 6,
      text: "gavel strike protocol execute security quarantine",
      description: "Finalizes low-latency shock-frequency vocal commands.",
      vocalProfileHint: "Deep authoritative command voice."
    }
  ];

  const [trainingActive, setTrainingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // 0 to 5
  const [trainingLogs, setTrainingLogs] = useState<string[]>([]);
  const [vocalAccuracies, setVocalAccuracies] = useState<number[]>(new Array(TRAINING_PHRASES.length).fill(0));
  const [isTrainingListening, setIsTrainingListening] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(() => {
    if (typeof window !== "undefined") {
      return Number(localStorage.getItem("nexorax_vcm_trained_progress") || "0");
    }
    return 0;
  });
  const [calibratedProfileName, setCalibratedProfileName] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("nexorax_vcm_profile_name") || "Standard Default Accent Profile";
    }
    return "Standard Default Accent Profile";
  });

  const isTrainingListeningRef = useRef(false);
  const currentStepRef = useRef(0);

  useEffect(() => {
    isTrainingListeningRef.current = isTrainingListening;
  }, [isTrainingListening]);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const calculatePhraseAccuracy = (spoken: string, target: string): number => {
    const cleanStr = (s: string) => s.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
    const sTokens = cleanStr(spoken).split(/\s+/).filter(Boolean);
    const tTokens = cleanStr(target).split(/\s+/).filter(Boolean);
    
    if (tTokens.length === 0) return 0;
    
    let matches = 0;
    tTokens.forEach(token => {
      // Direct matching or phonetic keyword contains
      if (sTokens.includes(token) || sTokens.some(st => st.includes(token) || token.includes(st))) {
        matches++;
      }
    });
    
    const accuracy = (matches / tTokens.length) * 100;
    return Math.min(100, Math.round(accuracy));
  };

  const evaluateTrainingPhrase = (spoken: string) => {
    const target = TRAINING_PHRASES[currentStepRef.current].text;
    const accuracy = calculatePhraseAccuracy(spoken, target);
    
    const stepLabel = `CALIBRATION PHRASE ${currentStepRef.current + 1}`;
    addLog(`CALIBRATION FEEDBACK [${stepLabel}]: Accuracy evaluated at ${accuracy}% against target.`);
    
    setTrainingLogs(prev => [
      `[${new Date().toLocaleTimeString()}] Analyzed: "${spoken}" — Accuracy: ${accuracy}%`,
      ...prev
    ]);

    setVocalAccuracies(prev => {
      const updated = [...prev];
      updated[currentStepRef.current] = accuracy;
      return updated;
    });

    if (accuracy >= 60) {
      speakText("Vocal segment matching verified.");
      addLog(`CALIBRATION APPROVED: Segment verified. Score locked.`);
    } else {
      speakText("Vocal matching score below threshold. Please attempt pronunciation calibration once more.");
    }
  };

  const simulateSpeechResult = (isPerfect: boolean = true) => {
    const target = TRAINING_PHRASES[currentStep].text;
    const spokenResult = isPerfect 
      ? target 
      : target.split(" ").slice(0, -1).join(" ") + " harmonic_interruption";
    
    setTranscript(spokenResult);
    evaluateTrainingPhrase(spokenResult);
  };

  const finalizeVocalCalibration = () => {
    localStorage.setItem("nexorax_vcm_trained_progress", "100");
    const formattedProfile = `Director ${calibratedProfileName.replace("Standard Default Accent Profile", "").trim() || "NexoraX"} Calibrated Accent Profile`;
    localStorage.setItem("nexorax_vcm_profile_name", formattedProfile);
    setCalibratedProfileName(formattedProfile);
    setTrainingProgress(100);
    setTrainingActive(false);
    
    addLog("VOCAL INTEGRITY MUTATION COMPLETE: Accuracy standard configured with custom user accent multipliers.");
    setAudioFeedbackText(`[CALIBRATION COMPLETE] Successfully locked training. Active voice models adjusted precisely for physical resonance profile: '${formattedProfile}'.`);
    speakText("Vocal profile locked. Security indices fully updated.");
  };
  // ================================================================

  const recognitionRef = useRef<any>(null);
  const isRecognitionActiveRef = useRef(false);

  // Sound and vocal synthesizer using personalized calibration attributes
  const speakText = (text: string) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedLanguage;
    utterance.rate = speechRate;
    utterance.pitch = speechPitch; // Optimized tone frequency
    window.speechSynthesis.speak(utterance);
  };

  const saveSettings = () => {
    localStorage.setItem("nexorax_vcm_confidence", minConfidence.toString());
    localStorage.setItem("nexorax_vcm_language", selectedLanguage);
    localStorage.setItem("nexorax_vcm_speech_rate", speechRate.toString());
    localStorage.setItem("nexorax_vcm_speech_pitch", speechPitch.toString());
    localStorage.setItem("nexorax_vcm_anc_boost", ancBoost ? "true" : "false");
    addLog(`SYSTEM PARAMETERS OPTIMIZED: Confidence threshold set to ${minConfidence}, language: ${selectedLanguage}`);
    speakText("Voice configuration parameters optimized and locked to hardware.");
  };

  const addLog = (msg: string) => {
    setCommandLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 50)]);
  };

  const executeCommandAction = (cmd: VoiceIntent) => {
    setLastAction(cmd.actionName);
    setAudioFeedbackText(`[SYSTEM GRANTED] Executing command '${cmd.actionName}': ${cmd.voiceFeedback}`);
    speakText(cmd.voiceFeedback);
    addLog(`MATCHED INTENT: '${cmd.actionName}' -> Dispatched ${cmd.systemAction}.`);

    // Dispatch custom event globally for other system layers to listen to
    const ev = new CustomEvent("nexorax-voice-action", {
      detail: {
        actionName: cmd.actionName,
        systemAction: cmd.systemAction,
        feedback: cmd.voiceFeedback
      }
    });
    window.dispatchEvent(ev);

    // Also dispatch tab switches if applicable
    if (cmd.systemAction === "ACTIVATE_VAULT") {
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "REALITY AUDIT LEDGER", landingTab: null }
      }));
    } else if (cmd.systemAction === "SCAN_INFRASTRUCTURE") {
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: "Home" }
      }));
    } else if (cmd.systemAction === "GENERATE_REPORT") {
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "REALITY AUDIT LEDGER", landingTab: null }
      }));
    } else if (cmd.systemAction === "CHECK_REGISTRY") {
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: "Home" }
      }));
    } else if (cmd.systemAction === "SHOW_KNOWLEDGE") {
      window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
        detail: { mainTab: "NODE 0: ARCHITECT TERMINAL", landingTab: null } // Show dashboard info
      }));
    } else if (cmd.systemAction === "HELP_AUTH") {
      window.dispatchEvent(new CustomEvent("nexorax-trigger-landing-tab", {
        detail: { tab: "Login" }
      }));
    }
  };

  const executeMacroStep = (step: any) => {
    addLog(`SEQ RUN: [Actuating Step Type: ${step.type}]`);
    
    switch (step.type) {
      case "OPEN_TAB":
        if (step.paramString) {
          window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
            detail: { mainTab: step.paramString, landingTab: step.landingTabUrl || null }
          }));
          if (step.landingTabUrl) {
            window.dispatchEvent(new CustomEvent("nexorax-trigger-landing-tab", {
              detail: { tab: step.landingTabUrl }
            }));
          }
          addLog(`Sequence Step Executed: Navigated to dashboard ${step.paramString} (subview: ${step.landingTabUrl || "default"}).`);
        }
        break;
      case "SET_OPACITY":
        if (typeof step.paramNumber === "number") {
          setVcmOpacity(step.paramNumber);
          addLog(`Sequence Step Executed: Adjusted display opacity standard to ${step.paramNumber * 100}%.`);
        }
        break;
      case "NOTIFY_SUPPORT":
        if (step.paramString) {
          addLog(`Sequence Step Executed: Dispatch support alert [Message: "${step.paramString}"].`);
          window.dispatchEvent(new CustomEvent("nexorax-support-notification", {
            detail: { message: step.paramString }
          }));
          setAudioFeedbackText(`[SUPPORT TRIGGERED] Notification dispatch: ${step.paramString}`);
        }
        break;
      case "TOGGLE_ANC_BOOST":
        setAncBoost(prev => {
          const newVal = !prev;
          addLog(`Sequence Step Executed: Noise Correction standard toggled to ${newVal ? "ON" : "OFF"}.`);
          return newVal;
        });
        break;
      case "SIMULATE_REVENUE_INFLOW":
        addLog("Sequence Step Executed: NexoraX revenue settlement impulse generated.");
        window.dispatchEvent(new CustomEvent("nexorax-simulate-revenue-pulse"));
        break;
      case "TRIGGER_GAVEL_ALARM":
        addLog("Sequence Step Executed: Instantaneous Gavel Strike Security Alarm triggered.");
        window.dispatchEvent(new CustomEvent("nexorax-gavel-alarm-trigger"));
        break;
      case "CALCULATE_NEXORAX_TAX":
        addLog("Sequence Step Executed: Auditing NexoraX global ledger status.");
        window.dispatchEvent(new CustomEvent("nexorax-calculate-tax-quick"));
        break;
      default:
        break;
    }
  };

  // Main custom command processing core with history audit logging hooks
  const processRawIntent = (rawText: string, confidenceVal?: number) => {
    const cleanText = rawText.toLowerCase().trim();
    if (!cleanText) return;

    addLog(`PROCESSING STREAM: "${rawText}"`);
    const currentConf = confidenceVal !== undefined ? confidenceVal : 0.95;

    // First try custom quick action matches
    const matchedQA = quickActions.find(qa => {
      const phrase = qa.customPhrase.toLowerCase().trim();
      return cleanText === phrase || cleanText.includes(phrase);
    });

    if (matchedQA) {
      addLog(`QUICK ACTION MACRO GRANTED: "${matchedQA.customPhrase}"`);
      setLastAction(matchedQA.customPhrase);
      const voiceFeedbackText = matchedQA.voiceResponse || `Actuating custom macro for ${matchedQA.customPhrase}`;
      setAudioFeedbackText(`[QUICK ACTION ACTUATED] '${matchedQA.customPhrase}': ${voiceFeedbackText}`);
      speakText(voiceFeedbackText);
      
      // Handle the target Navigation (only for simple macros or legacy)
      if (matchedQA.macroAction !== "COMPLEX_SEQUENCE") {
        if (matchedQA.targetTab) {
          window.dispatchEvent(new CustomEvent("nexorax-voice-navigate", {
            detail: { mainTab: matchedQA.targetTab, landingTab: matchedQA.landingTab }
          }));
        }
        if (matchedQA.landingTab) {
          window.dispatchEvent(new CustomEvent("nexorax-trigger-landing-tab", {
            detail: { tab: matchedQA.landingTab }
          }));
        }

        // Handle the macro actions
        if (matchedQA.macroAction === "TOGGLE_ANC_BOOST") {
          setAncBoost(prev => !prev);
          addLog("MACRO: Active Noise Correction parameter toggled.");
        } else if (matchedQA.macroAction === "SIMULATE_REVENUE_INFLOW") {
          addLog("MACRO: Simulating NexoraX Revenue Settlement Inflow...");
          window.dispatchEvent(new CustomEvent("nexorax-simulate-revenue-pulse"));
        } else if (matchedQA.macroAction === "TRIGGER_GAVEL_ALARM") {
          addLog("MACRO: Spawning Gavel Strike Security Alarm...");
          window.dispatchEvent(new CustomEvent("nexorax-gavel-alarm-trigger"));
        } else if (matchedQA.macroAction === "CALCULATE_NEXORAX_TAX") {
          addLog("MACRO: Auditing instantaneous global ledger balance.");
          window.dispatchEvent(new CustomEvent("nexorax-calculate-tax-quick"));
        }
      }

      // Handle sequence of actions sequentially
      if (matchedQA.sequenceSteps && matchedQA.sequenceSteps.length > 0) {
        addLog(`SEQUENCE ACTIONS ACTIVE: Running ${matchedQA.sequenceSteps.length} sequence elements...`);
        matchedQA.sequenceSteps.forEach((step, idx) => {
          setTimeout(() => {
            executeMacroStep(step);
          }, idx * 450); // elegant and paced 450ms sequential delays
        });
      }

      addHistoryRecord(rawText, `Macro: ${matchedQA.customPhrase}`, currentConf, "GRANTED", "Custom Quick Actions");
      return;
    }

    // Try to find matching intent keyword
    let foundCmd = REGISTERED_VOICE_COMMANDS.find(cmd => {
      return cmd.intents.some(phrase => cleanText === phrase || cleanText.includes(phrase));
    });

    if (foundCmd) {
      executeCommandAction(foundCmd);
      addHistoryRecord(rawText, foundCmd.actionName, currentConf, "GRANTED", foundCmd.category);
    } else {
      // Test-case fallback for flexible keyword matching
      let fallbackCmd = null;
      if (cleanText.includes("vault") || cleanText.includes("open vault")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "open-vault");
      } else if (cleanText.includes("infrastructure") || cleanText.includes("scan")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "scan-infra");
      } else if (cleanText.includes("report") || cleanText.includes("generate")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "gen-report");
      } else if (cleanText.includes("status")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "sys-status");
      } else if (cleanText.includes("knowledge") || cleanText.includes("document")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "kd-base");
      } else if (cleanText.includes("customer support") || cleanText.includes("support")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "cust-support");
      } else if (cleanText.includes("q and a") || cleanText.includes("qa") || cleanText.includes("question")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "inbound-qa");
      } else if (cleanText.includes("login") || cleanText.includes("join") || cleanText.includes("sign")) {
        fallbackCmd = REGISTERED_VOICE_COMMANDS.find(c => c.id === "help-login-join");
      }

      if (fallbackCmd) {
        executeCommandAction(fallbackCmd);
        addHistoryRecord(rawText, fallbackCmd.actionName, currentConf, "GRANTED", fallbackCmd.category);
      } else {
        addLog(`INTENT UNRESOLVED: Did not match standard patterns.`);
        setAudioFeedbackText(`[UNRESOLVED] "${rawText}" did not match an active system keyword. Click below or speak one of the listed commands.`);
        speakText("NexoraX voice index does not recognize this instruction. Please select one of the valid command nodes.");
        addHistoryRecord(rawText, "Unmapped Directive", currentConf, "UNRESOLVED", "Unmapped");
      }
    }
  };

  // Start real browser speech recognition if permitted
  const startBrowserSpeech = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      addLog("Speech API unsupported. Standardizing simulation link input.");
      setAudioFeedbackText("Web Speech API is unsupported in this sandboxed context. Real-time microphone capture can be simulated using the direct transmission triggers below.");
      return;
    }

    try {
      const rec = new SpeechRecognition();
      recognitionRef.current = rec;
      rec.continuous = false;
      rec.interimResults = false;
      rec.lang = selectedLanguage;

      rec.onstart = () => {
        isRecognitionActiveRef.current = true;
        setIsListening(true);
        setTranscript("");
        addLog(`SPEECH INTEGRATOR ACTIVE: Capturing live audio spectrum via ${selectedLanguage}...`);
      };

      rec.onresult = (event: any) => {
        const result = event.results[0][0];
        const spoken = result.transcript;
        const confidence = result.confidence || 0.95;

        setTranscript(spoken);
        addLog(`FREQUENCY TRANSLATED: "${spoken}" [Confidence: ${(confidence * 100).toFixed(1)}%]`);

        if (isTrainingListeningRef.current) {
          evaluateTrainingPhrase(spoken);
        } else {
          if (confidence >= minConfidence) {
            processRawIntent(spoken, confidence);
          } else {
            addLog(`INTENT BLOCKED: Confidence level ${confidence.toFixed(2)} is below minimum threshold ${minConfidence}`);
            setAudioFeedbackText(`[REJECTED - LOW CONFIDENCE] Spoke "${spoken}" but match confidence (${(confidence * 100).toFixed(0)}%) is below the configured ${minConfidence * 100}% sensitivity threshold.`);
            speakText("Low signal match accuracy. Please calibrate or increase speaker clarity.");
            addHistoryRecord(spoken, "None (Low Confidence)", confidence, "BLOCKED", "Noise/Interference");
          }
        }
      };

      rec.onend = () => {
        isRecognitionActiveRef.current = false;
        setIsListening(false);
      };

      rec.onerror = (evt: any) => {
        isRecognitionActiveRef.current = false;
        setIsListening(false);
        addLog(`SPEECH RECOGNITION EXCEPTION: ${evt.error}`);
        if (evt.error === "not-allowed") {
          setAudioFeedbackText("Microphone permission denied. Using high-fidelity local software triggers instead.");
        }
      };

      rec.start();
    } catch (e) {
      console.warn("Speech start exception context:", e);
    }
  };

  const stopBrowserSpeech = () => {
    if (recognitionRef.current && isRecognitionActiveRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {}
    }
    setIsListening(false);
  };

  const toggleListening = () => {
    if (isListening) {
      stopBrowserSpeech();
    } else {
      startBrowserSpeech();
    }
  };

  // Global Mute/Unmute hotkey 'Alt + V' toggle listener
  useEffect(() => {
    const handleGlobalHotkey = (e: KeyboardEvent) => {
      // Check if user is typing in form inputs, textareas, or select dropdowns to avoid hijacking standard inputs
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.tagName === "SELECT")
      ) {
        return;
      }

      if (e.altKey && (e.key === "v" || e.key === "V")) {
        e.preventDefault();
        addLog("[HOTKEY ACTUATION] Alt + V triggered global microphone state toggle.");
        toggleListening();
      }
    };

    window.addEventListener("keydown", handleGlobalHotkey);
    return () => {
      window.removeEventListener("keydown", handleGlobalHotkey);
    };
  }, [isListening, selectedLanguage, minConfidence]);

  const handleSimulatedTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!textToProcess.trim()) return;
    processRawIntent(textToProcess);
    setTextToProcess("");
  };

  const categories = ["ALL", "Security & Vault", "Infrastructure", "Intelligence & Reports", "Support & Assistance"];
  const filteredCommands = selectedCategory === "ALL" 
    ? REGISTERED_VOICE_COMMANDS 
    : REGISTERED_VOICE_COMMANDS.filter(cmd => cmd.category === selectedCategory);

  return (
    <div 
      id="voice-command-manager-wrapper" 
      style={{ opacity: vcmOpacity, transition: "opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1)" }}
      className="p-5 bg-black/60 border border-indigo-500/30 rounded-xl max-w-5xl mx-auto space-y-6 shadow-[0_0_30px_rgba(99,102,241,0.15)] relative overflow-hidden backdrop-blur-md"
    >
      
      {/* Decorative Grid Lines */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-grid bg-[size:16px_16px]"></div>
      
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-indigo-500/20 pb-4 relative z-10">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-ping"></span>
            <span className="text-[10px] font-mono font-bold text-indigo-400 tracking-widest uppercase">NODE-09.2 AUTONOMOUS VOICE ENGINE</span>
          </div>
          <h2 id="vcm-main-title" className="text-xl font-bold tracking-tight text-white font-sans mt-1">NexoraX Voice Command Manager</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl font-sans">
            Interactive speech matrix providing absolute hands-free navigation and corporate response routing configured for 0.8s execution latency.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-3">
          <button
            id="vcm-mic-trigger"
            type="button"
            onClick={toggleListening}
            className={`px-4 py-2.5 rounded-lg border font-mono text-xs font-bold uppercase transition-all duration-300 flex items-center gap-2 cursor-pointer shadow-lg ${
              isListening 
                ? "bg-red-500/20 border-red-500 text-red-200 animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.3)]" 
                : "bg-indigo-900/40 border-indigo-500/50 hover:bg-indigo-500/20 text-indigo-300 hover:text-white"
            }`}
          >
            {isListening ? <MicOff className="w-4 h-4 text-red-400" /> : <Mic className="w-4 h-4 text-indigo-400" />}
            <span>{isListening ? "DEACTIVATE MIC" : "ACTIVATE DIRECT MIC"}</span>
            <span className="text-[10px] opacity-60 ml-1 font-mono font-normal">alt+v</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
        
        {/* Left Side: Waveform and Interactive Audio Feedback Console */}
        <div id="vcm-left-deck" className="lg:col-span-5 space-y-4 flex flex-col">
          
          {/* Visual Waveform Indicator of listening state */}
          <div id="vcm-waveform-panel" className="flex-1 min-h-[160px] p-4 bg-zinc-950/90 border border-zinc-800/80 rounded-lg flex flex-col justify-between overflow-hidden relative">
            <span className="text-[9px] font-mono text-zinc-500 tracking-widest uppercase block">NEURAL SPECTRUM FEEDBACK</span>
            
            {/* Real Waveform simulation lines */}
            <div className="h-24 flex items-center justify-center gap-1 my-2">
              {Array.from({ length: 28 }).map((_, i) => {
                const baseHeight = isListening ? 14 : 4;
                const randomHeightModifier = isListening ? Math.sin((i + Date.now()/100)) * 32 + 40 : Math.cos(i) * 5 + 6;
                const duration = 0.5 + (i % 5) * 0.1;
                return (
                  <motion.div
                    key={i}
                    id={`vcm-waveform-bar-${i}`}
                    className={`w-1 rounded-full ${isListening ? "bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]" : "bg-zinc-800"}`}
                    animate={{
                      height: isListening 
                        ? [baseHeight, randomHeightModifier, baseHeight] 
                        : [baseHeight, baseHeight + (i % 3), baseHeight]
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: isListening ? duration : 1.2,
                      ease: "easeInOut"
                    }}
                    style={{ height: `${baseHeight}px` }}
                  />
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t border-zinc-800/50 pt-2 text-[10px] font-mono">
              <span className="text-zinc-500">MAPPING ACCURACY</span>
              <span className={isListening ? "text-indigo-400 font-bold" : "text-zinc-600"}>
                {isListening ? "CAPTURING LIVE SPECTRAL LEVEL: 99.8%" : "MICROPHONE STANDBY"}
              </span>
            </div>
          </div>

          {/* Interactive feedback output display card */}
          <div id="vcm-audio-feedback-card" className="p-4 bg-indigo-950/20 border border-indigo-500/10 rounded-lg space-y-2">
            <span className="text-[9px] font-mono text-indigo-400 tracking-widest uppercase block">SYSTEM VOCAL ANCHOR RESPONSE</span>
            <div id="vcm-feedback-output" className="text-xs font-mono text-slate-200 leading-relaxed bg-zinc-950/60 p-3 border border-indigo-500/5 rounded min-h-[60px] flex items-start gap-2">
              <Activity className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5 animate-pulse" />
              <span>{audioFeedbackText}</span>
            </div>
            {transcript && (
              <div className="text-[10px] font-mono text-zinc-500">
                Spoken Input Translate: <span className="text-[#e240f3] font-bold">"{transcript}"</span>
              </div>
            )}
          </div>

          {/* Simulated Speech Handshake Inputs */}
          <div id="vcm-input-console" className="p-4 bg-zinc-950/60 border border-zinc-800/80 rounded-lg space-y-3">
            <span className="text-[9px] font-mono text-zinc-500 tracking-widest uppercase block">SIMULATE OR SUBMIT WRITTEN PHRASE</span>
            <form onSubmit={handleSimulatedTextSubmit} className="flex gap-2">
              <input
                id="vcm-console-input"
                type="text"
                placeholder="Type 'open vault', 'system status', or 'help on login'..."
                value={textToProcess}
                onChange={(e) => setTextToProcess(e.target.value)}
                className="flex-1 bg-black/60 border border-zinc-800 focus:border-indigo-500 focus:outline-none rounded px-3 py-1.5 text-xs font-mono text-zinc-200"
              />
              <button
                id="vcm-console-submit"
                type="submit"
                className="px-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-mono font-bold uppercase transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                <Send className="w-3 h-3" />
                TEST
              </button>
            </form>
          </div>

          {/* NexoraX Calibration Settings Panel */}
          <div id="vcm-settings-panel" className="p-4 bg-zinc-950/80 border border-indigo-500/10 rounded-lg space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-indigo-400" />
                <span className="text-[10px] font-mono font-bold text-indigo-300 tracking-wider uppercase">ARCHITECT ENGINE CALIBRATION</span>
              </div>
              <button
                type="button"
                onClick={saveSettings}
                className="px-2.5 py-1 bg-indigo-600/30 hover:bg-indigo-600 border border-indigo-500 text-white font-mono text-[9px] font-bold rounded uppercase transition-all duration-200 cursor-pointer shadow-md"
              >
                LOCK SETTINGS
              </button>
            </div>

            <div className="space-y-3.5">
              {/* Language Selector */}
              <div className="space-y-1">
                <label className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider block font-bold">Preferred Language / Accent</label>
                <select
                  id="vcm-language-selector"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="en-GB">English (British Standard) - en-GB</option>
                  <option value="en-US">English (US Accent) - en-US</option>
                  <option value="en-AU">English (Australian Accent) - en-AU</option>
                  <option value="en-IN">English (Indian Accent) - en-IN</option>
                  <option value="ar-SA">Arabic (Saudi Arabia) - ar-SA</option>
                  <option value="es-ES">Spanish (Spain Castilian) - es-ES</option>
                  <option value="fr-FR">French (France) - fr-FR</option>
                  <option value="de-DE">German (Germany) - de-DE</option>
                </select>
              </div>

              {/* Confidence Threshold */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[9px] font-mono text-zinc-400 uppercase tracking-wider font-bold">
                  <span>Recognition Sensitivity</span>
                  <span className="text-indigo-400 font-bold">{(minConfidence * 100).toFixed(0)}% Threshold</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="vcm-sensitivity-slider"
                    type="range"
                    min="0.1"
                    max="0.9"
                    step="0.05"
                    value={minConfidence}
                    onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
                    className="flex-1 accent-indigo-500 bg-zinc-900 h-1.5 rounded cursor-pointer"
                  />
                  <span className="text-[10px] font-mono text-zinc-500 select-none w-12 text-right">
                    {minConfidence < 0.35 ? "High Sens" : minConfidence > 0.65 ? "Strict" : "Standard"}
                  </span>
                </div>
              </div>

              {/* Synthesizer Speed & Pitch Calibration */}
              <div className="grid grid-cols-2 gap-3 pb-1 border-t border-zinc-900 pt-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[9px] font-mono text-zinc-400 uppercase tracking-wider font-bold">
                    <span>Accent Speed</span>
                    <span className="text-zinc-500">{speechRate.toFixed(2)}x</span>
                  </div>
                  <input
                    id="vcm-speed-slider"
                    type="range"
                    min="0.6"
                    max="1.6"
                    step="0.05"
                    value={speechRate}
                    onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                    className="w-full accent-indigo-500 bg-zinc-900 h-1 rounded cursor-pointer"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[9px] font-mono text-zinc-400 uppercase tracking-wider font-bold">
                    <span>Accent Pitch</span>
                    <span className="text-zinc-500">{speechPitch.toFixed(2)}v</span>
                  </div>
                  <input
                    id="vcm-pitch-slider"
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.05"
                    value={speechPitch}
                    onChange={(e) => setSpeechPitch(parseFloat(e.target.value))}
                    className="w-full accent-indigo-500 bg-zinc-900 h-1 rounded cursor-pointer"
                  />
                </div>
              </div>

              {/* Active Noise Cancellation Toggle */}
              <div className="flex items-center justify-between pt-2.5 border-t border-zinc-900">
                <div className="flex flex-col">
                  <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider font-bold">Active Noise Correction (ANC)</span>
                  <span className="text-[8px] text-zinc-600 font-sans">Suppresses secondary vocal signals inside sandboxed browser context.</span>
                </div>
                <button
                  id="vcm-anc-toggle"
                  type="button"
                  onClick={() => setAncBoost(!ancBoost)}
                  className={`px-2 py-0.5 rounded font-mono text-[9px] border transition-colors cursor-pointer ${
                    ancBoost 
                      ? "bg-emerald-950/20 border-emerald-500 text-emerald-400 font-bold" 
                      : "bg-zinc-900 border-zinc-800 text-zinc-500"
                  }`}
                >
                  {ancBoost ? "ANC: ACTIVE" : "ANC: BYPASS"}
                </button>
              </div>

            </div>
          </div>

          {/* NexoraX Voice Training Calibration Matrix */}
          <div id="vcm-voice-training-panel" className="p-4 bg-zinc-950/90 border border-[#e240f3]/20 hover:border-[#e240f3]/40 rounded-lg space-y-4 transition-all duration-300">
            <div className="flex items-center justify-between border-b border-zinc-850 pb-2.5">
              <div className="flex items-center gap-2 font-mono">
                <UserCheck className="w-4 h-4 text-[#e240f3]" />
                <div>
                  <span className="text-[10px] font-mono font-bold text-[#e240f3] tracking-wider uppercase block">VOX INTELLIGENCE: NEXORAX VOCAL TRAINING</span>
                  <p className="text-[8px] text-zinc-500 font-mono uppercase">Calibrate 6 Specific Phrases to Customize Neural Voice Mapping</p>
                </div>
              </div>
              
              {!trainingActive && (
                <button
                  id="vcm-initiate-training-btn"
                  type="button"
                  onClick={() => {
                    setTrainingActive(true);
                    setCurrentStep(0);
                    setVocalAccuracies(new Array(TRAINING_PHRASES.length).fill(0));
                    setTrainingLogs([`[${new Date().toLocaleTimeString()}] Vocal calibration matrix initialized.`]);
                    speakText("Initializing vocal calibration sequence. Please read the displayed phrases.");
                  }}
                  className="px-2 py-1 bg-[#e240f3]/10 hover:bg-[#e240f3] border border-[#e240f3]/30 text-[#e240f3] hover:text-black font-mono text-[9px] font-bold rounded uppercase transition-all duration-200 cursor-pointer"
                >
                  START CALIBRATION
                </button>
              )}
            </div>

            {/* Current Active Calibration Profile Summary */}
            <div className="bg-zinc-900/40 p-2.5 rounded border border-zinc-900 flex flex-col gap-2">
              <div className="flex items-center justify-between text-[9px] font-mono">
                <span className="text-zinc-500">ACTIVE FOOTPRINT PROFILE</span>
                <span className="text-emerald-400 font-bold uppercase">{calibratedProfileName}</span>
              </div>
              <div>
                <div className="flex justify-between text-[8px] font-mono text-zinc-500 uppercase mb-1">
                  <span>Calibration Accuracy Standard</span>
                  <span className="text-indigo-400 font-semibold">{trainingProgress}% Calibrated</span>
                </div>
                <div className="w-full bg-zinc-950 h-1.5 rounded-full overflow-hidden border border-zinc-850">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 via-[#e240f3] to-emerald-400 transition-all duration-500" 
                    style={{ width: `${trainingProgress}%` }}
                  />
                </div>
              </div>
            </div>

            {trainingActive ? (
              <div className="space-y-4">
                {/* Active phrase progress card */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] font-mono text-zinc-500 uppercase font-bold">
                      Progress Phase: {currentStep + 1} of {TRAINING_PHRASES.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setTrainingActive(false);
                        setIsTrainingListening(false);
                        addLog("CALIBRATION TERMINATED: Voice session stopped.");
                      }}
                      className="text-[8px] font-mono text-rose-400 hover:text-rose-300 uppercase cursor-pointer"
                    >
                      Abort Calibration
                    </button>
                  </div>

                  {/* Highlighted text box with specific custom voice phrase target */}
                  <div className="p-3.5 bg-black border border-dashed border-[#e240f3]/30 rounded-lg flex flex-col gap-2 select-all hover:bg-zinc-950 transition-colors">
                    <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block text-center">READ THIS OUT LOUD:</span>
                    <div className="text-center font-mono font-bold text-xs text-white tracking-wide">
                      "{TRAINING_PHRASES[currentStep].text}"
                    </div>
                    <div className="pt-2 border-t border-zinc-900 text-center space-y-1">
                      <p className="text-[10px] text-zinc-400 font-sans italic">
                        {TRAINING_PHRASES[currentStep].description}
                      </p>
                      <p className="text-[8px] text-zinc-600 font-mono uppercase">
                        Vocal Hint: {TRAINING_PHRASES[currentStep].vocalProfileHint}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Accuracy state of latest match */}
                {vocalAccuracies[currentStep] > 0 && (
                  <div className="p-2 bg-zinc-900/60 border border-zinc-800 rounded flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono">
                      <span className="text-zinc-500">Evaluation Result:</span>
                      <span className={`font-bold uppercase ${vocalAccuracies[currentStep] >= 60 ? "text-emerald-400" : "text-rose-400"}`}>
                        {vocalAccuracies[currentStep]}% Match
                      </span>
                    </div>
                    <span className={`text-[8.5px] font-mono uppercase px-1.5 py-0.2 rounded font-bold ${
                      vocalAccuracies[currentStep] >= 60 
                        ? "bg-emerald-950/40 text-emerald-400 border border-emerald-500/10" 
                        : "bg-rose-950/40 text-rose-400 border border-rose-500/10"
                    }`}>
                      {vocalAccuracies[currentStep] >= 60 ? "VERIFIED (LOCKED)" : "LOW QUALITY MATCH (TRY AGAIN)"}
                    </span>
                  </div>
                )}

                {/* Voice Action training row */}
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2">
                    {/* Live microphone calibration capture button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (isTrainingListening) {
                          setIsTrainingListening(false);
                          stopBrowserSpeech();
                        } else {
                          setIsTrainingListening(true);
                          startBrowserSpeech();
                        }
                      }}
                      className={`flex-1 py-1.5 rounded font-mono text-xs font-bold uppercase border transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer ${
                        isTrainingListening 
                          ? "bg-[#e240f3]/25 border-[#e240f3] text-zinc-100 animate-pulse shadow-[0_0_10px_rgba(226,64,243,0.2)]" 
                          : "bg-[#e240f3]/10 hover:bg-[#e240f3]/25 border-[#e240f3]/30 text-zinc-300"
                      }`}
                    >
                      <Mic className="w-3.5 h-3.5 text-[#e240f3]" />
                      {isTrainingListening ? "CAPTURING HARMONICS..." : "CAPTURE SPEECH MIC"}
                    </button>

                    {/* Simulators */}
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => simulateSpeechResult(true)}
                        className="px-2.5 py-1.5 bg-emerald-950/30 hover:bg-emerald-600 hover:text-white border border-emerald-500/10 text-emerald-400 text-[9px] font-mono uppercase rounded transition-colors cursor-pointer"
                        title="Simulate 100% matched vocal accuracy"
                      >
                        Simulate Match
                      </button>
                      <button
                        type="button"
                        onClick={() => simulateSpeechResult(false)}
                        className="px-2.5 py-1.5 bg-rose-950/30 hover:bg-rose-600 hover:text-white border border-rose-500/10 text-rose-400 text-[9px] font-mono uppercase rounded transition-colors cursor-pointer"
                        title="Simulate failed low similarity input alignment"
                      >
                        Simulate Noise
                      </button>
                    </div>
                  </div>

                  {/* Calibration Nav controls */}
                  <div className="flex gap-2 justify-between border-t border-zinc-900 pt-3">
                    <button
                      type="button"
                      disabled={currentStep === 0}
                      onClick={() => {
                        setCurrentStep(prev => prev - 1);
                        setIsTrainingListening(false);
                        setTranscript("");
                      }}
                      className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-850 border border-zinc-850 text-zinc-400 text-[9px] font-mono uppercase rounded cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
                    >
                      Previous Phrase
                    </button>

                    {currentStep < TRAINING_PHRASES.length - 1 ? (
                      <button
                        type="button"
                        disabled={vocalAccuracies[currentStep] < 60}
                        onClick={() => {
                          setCurrentStep(prev => prev + 1);
                          setIsTrainingListening(false);
                          setTranscript("");
                          speakText("Proceeding to phase " + (currentStep + 2) + ".");
                        }}
                        className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 border border-indigo-500 text-white text-[9px] font-mono uppercase rounded font-bold cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
                      >
                        Next Phrase
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={vocalAccuracies[currentStep] < 60}
                        onClick={finalizeVocalCalibration}
                        className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-[9px] font-mono uppercase rounded font-black cursor-pointer disabled:opacity-30 disabled:pointer-events-none shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                      >
                        Finalize Vocal Signature
                      </button>
                    )}
                  </div>
                </div>

                {/* Mini Calibration execution terminal */}
                <div className="space-y-1">
                  <span className="text-[8px] font-mono text-zinc-600 uppercase tracking-wider block">CALIBRATION REVERBERATION INDEX LOGS:</span>
                  <div className="p-2.5 bg-black border border-zinc-900 rounded font-mono text-[9px] text-zinc-500 h-20 overflow-y-auto custom-scrollbar space-y-1.5">
                    {trainingLogs.length === 0 ? (
                      <div className="text-zinc-700 italic">No logs created in current cycle.</div>
                    ) : (
                      trainingLogs.map((logStr, idx) => (
                        <div key={idx} className="truncate select-all text-slate-500">
                          {logStr}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-900/15 border border-dashed border-zinc-900 rounded flex items-center justify-between gap-4">
                <p className="text-[9.5px] font-mono text-zinc-500 leading-normal max-w-sm">
                  CALIBRATED PROFILE ACTIVE: <span className="text-[#e240f3] font-semibold">{calibratedProfileName}</span>. 
                  Vocal profile helps stabilize inputs under noisy environments. Click "START CALIBRATION" to recalibrate anytime.
                </p>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[8px] font-mono text-zinc-600 uppercase text-right block font-bold">Profile Rename</span>
                  <input
                    id="vcm-custom-profile-input"
                    type="text"
                    value={calibratedProfileName.replace("Director ", "").replace(" Calibrated Accent Profile", "")}
                    placeholder="Signature Name"
                    onChange={(e) => {
                      const val = e.target.value;
                      const full = val ? `Director ${val} Calibrated Accent Profile` : "Standard Default Accent Profile";
                      setCalibratedProfileName(full);
                      localStorage.setItem("nexorax_vcm_profile_name", full);
                    }}
                    className="bg-black border border-zinc-805 text-[8.5px] font-mono px-2 py-0.5 rounded text-zinc-300 w-28 outline-none focus:border-[#e240f3]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Command execution logs */}
          <div id="vcm-activity-logs" className="p-4 bg-zinc-950/90 border border-zinc-900 rounded-lg min-h-[140px] flex flex-col justify-between">
            <span className="text-[9px] font-mono text-zinc-500 tracking-widest uppercase block mb-2">IMMUTABLE REVOLUTION REVENUE LEDGER & VOICE STATE SYNC</span>
            <div className="space-y-1.5 overflow-y-auto max-h-[120px] custom-scrollbar text-[9px] font-mono text-zinc-400">
              {commandLogs.map((log, idx) => (
                <div key={idx} className="truncate select-all text-slate-400 hover:text-white border-l border-zinc-800 pl-1.5 py-0.5">
                  {log}
                </div>
              ))}
            </div>
          </div>

          {/* Command History / Audit Log */}
          <div id="vcm-history-panel" className="p-4 bg-zinc-950/90 border border-[#e240f3]/10 rounded-lg space-y-3">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#e240f3]" />
                <span className="text-[10px] font-mono font-bold text-[#e240f3] tracking-wider uppercase">AUDIT LOG: LAST 10 VOICE INTENTS</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRecognizedHistory([]);
                  localStorage.removeItem("nexorax_vcm_history");
                  addLog("AUDIT LEDGER CLEARED: All past session history wiped.");
                }}
                className="text-[8px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors uppercase border border-zinc-800 rounded px-1.5 py-0.5 cursor-pointer"
              >
                CLEAR LOGS
              </button>
            </div>

            <div className="space-y-2 overflow-y-auto max-h-[220px] custom-scrollbar">
              {recognizedHistory.length === 0 ? (
                <div className="text-center py-6 text-zinc-600 font-mono text-[10px]">
                  NO RECORDED VOICE COMMANDS FOUND IN CURRENT SESSION AUDIT
                </div>
              ) : (
                recognizedHistory.map((item, idx) => (
                  <div
                    key={idx}
                    id={`vcm-history-item-${idx}`}
                    className="p-2.5 bg-black/40 border border-zinc-900/60 rounded flex flex-col gap-1 hover:border-zinc-800 transition-colors"
                  >
                    <div className="flex items-center justify-between text-[9px] font-mono">
                      <div className="flex items-center gap-1.5">
                        <span className="text-zinc-500">{item.timestamp}</span>
                        <span className="text-zinc-700">|</span>
                        <span className="text-zinc-400 capitalize">{item.category}</span>
                      </div>
                      <span
                        className={`px-1.5 py-0.2 rounded-full font-bold text-[8px] uppercase tracking-wider ${
                          item.status === "GRANTED"
                            ? "bg-emerald-950/40 text-emerald-400 border border-emerald-500/20"
                            : item.status === "BLOCKED"
                            ? "bg-rose-950/40 text-rose-400 border border-rose-500/20"
                            : "bg-amber-950/40 text-amber-400 border border-amber-500/20"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>

                    <div className="text-xs font-mono text-zinc-200 break-all">
                      Spoke: <span className="text-indigo-300">"{item.intentSpoken}"</span>
                    </div>

                    <div className="flex items-center justify-between text-[9px] font-mono text-zinc-500">
                      <div>
                        Action: <span className="text-zinc-300 font-semibold">{item.matchedAction}</span>
                      </div>
                      <div>
                        Accuracy: <span className="text-indigo-400 font-semibold">{(item.confidence * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="text-[8px] font-mono text-zinc-600 text-right uppercase">
              CONFIDENTIAL CRYPTO-AUDIT STREAMED TO NexoraX Resource Library
            </div>
          </div>

        </div>

        {/* Right Side: Command Dictionary Deck & Interactive Fast-Test Triggers */}
        <div id="vcm-right-deck" className="lg:col-span-7 space-y-4">
          
          {/* Quick Actions & Navigation Macros Configurator Deck */}
          <div id="vcm-quick-actions-panel" className="p-4 bg-zinc-950/90 border border-[#e240f3]/10 rounded-lg space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-850 pb-2.5">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#e240f3] animate-pulse" />
                <div>
                  <span className="text-[10px] font-mono font-bold text-[#e240f3] tracking-wider uppercase block">VOICE MACROS & QUICK ACTIONS</span>
                  <p className="text-[8px] text-zinc-500 font-mono uppercase">Bind Custom Voice Intents to Navigation Keys & Macros</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  id="vcm-toggle-macro-form"
                  type="button"
                  onClick={() => setShowConfigForm(!showConfigForm)}
                  className="px-2 py-0.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 font-mono text-[9px] font-bold rounded uppercase transition-all duration-200 cursor-pointer flex items-center gap-1"
                >
                  <Plus className="w-2.5 h-2.5" />
                  {showConfigForm ? "Close Form" : "Create Macro"}
                </button>
                <button
                  id="vcm-create-sequenced-macro"
                  type="button"
                  onClick={() => {
                    const tempMacro: QuickActionMacro = {
                      id: "qa-" + Date.now(),
                      customPhrase: "custom sequence " + (quickActions.length + 1),
                      targetTab: "NODE 0: ARCHITECT TERMINAL",
                      landingTab: null,
                      macroAction: "COMPLEX_SEQUENCE",
                      voiceResponse: "Custom sequenced macro activated.",
                      sequenceSteps: [
                        { id: "step-" + Date.now() + "1", type: "OPEN_TAB", paramString: "NODE 0: ARCHITECT TERMINAL", landingTabUrl: "Home" }
                      ]
                    };
                    openEditorModal(tempMacro);
                  }}
                  className="px-2 py-0.5 bg-[#e240f3]/25 hover:bg-[#e240f3] border border-[#e240f3]/30 text-[#e240f3] hover:text-black font-mono text-[9px] font-bold rounded uppercase transition-all duration-200 cursor-pointer flex items-center gap-1"
                >
                  <Layers className="w-2.5 h-2.5" />
                  Create Sequence
                </button>
              </div>
            </div>

            {/* Collapsible Configurator Form */}
            {showConfigForm && (
              <form
                id="vcm-quick-action-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!newPhrase.trim()) return;
                  
                  const newMacro: QuickActionMacro = {
                    id: "qa-" + Date.now(),
                    customPhrase: newPhrase.trim().toLowerCase(),
                    targetTab: newTargetTab,
                    landingTab: newLandingTab.trim() || null,
                    macroAction: newMacroAction,
                    voiceResponse: newVoiceResponse.trim() || `Actuating custom action for ${newPhrase}`
                  };
                  
                  const updated = [newMacro, ...quickActions];
                  saveQuickActionsList(updated);
                  addLog(`QUICK ACTION ADDED: [Spoken Trigger: "${newPhrase}"] mapped successfully.`);
                  speakText("New spoken macro registered.");
                  
                  // Reset states
                  setNewPhrase("");
                  setNewLandingTab("");
                  setNewVoiceResponse("");
                  setNewMacroAction("NONE");
                  setShowConfigForm(false);
                }}
                className="p-3 bg-zinc-900/60 border border-zinc-850 rounded space-y-3"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Custom Phrase Input */}
                  <div className="space-y-1">
                    <label className="text-[8px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">Custom Voice Trigger Phrase</label>
                    <input
                      id="vcm-new-macro-phrase"
                      type="text"
                      required
                      placeholder="e.g. override network core"
                      value={newPhrase}
                      onChange={(e) => setNewPhrase(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] placeholder-zinc-700 outline-none"
                    />
                  </div>

                  {/* Target Navigation Tab Selector */}
                  <div className="space-y-1">
                    <label className="text-[8px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">Target UI Dispatch Tab</label>
                    <select
                      id="vcm-new-macro-target"
                      value={newTargetTab}
                      onChange={(e) => setNewTargetTab(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] outline-none"
                    >
                      <option value="NODE 0: ARCHITECT TERMINAL">NODE 0: ARCHITECT TERMINAL</option>
                      <option value="REALITY AUDIT LEDGER">REALITY AUDIT LEDGER</option>
                      <option value="NEXORAX WORKPLACE">NEXORAX WORKPLACE</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Optional Subview/Landing View Name */}
                  <div className="space-y-1">
                    <label className="text-[8px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">Subview / Sub-tab Key (Optional)</label>
                    <input
                      id="vcm-new-macro-landing"
                      type="text"
                      placeholder="e.g. Home, Login, Join"
                      value={newLandingTab}
                      onChange={(e) => setNewLandingTab(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] placeholder-zinc-700 outline-none"
                    />
                  </div>

                  {/* Automated Action Macro Select */}
                  <div className="space-y-1">
                    <label className="text-[8px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">Automated Macro Payload Trigger</label>
                    <select
                      id="vcm-new-macro-action"
                      value={newMacroAction}
                      onChange={(e) => setNewMacroAction(e.target.value as any)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] outline-none text-indigo-400"
                    >
                      <option value="NONE">NONE (Navigation Only)</option>
                      <option value="TOGGLE_ANC_BOOST">TOGGLE_ANC_BOOST (Toggle Noise Correction)</option>
                      <option value="SIMULATE_REVENUE_INFLOW">SIMULATE_REVENUE_INFLOW (Dispatches simulated Secure Payment Provider Pulse)</option>
                      <option value="TRIGGER_GAVEL_ALARM">TRIGGER_GAVEL_ALARM (Fire Gavel Strike Security Alarm)</option>
                      <option value="CALCULATE_NEXORAX_TAX">CALCULATE_NEXORAX_TAX (Instant Ledger Audit)</option>
                    </select>
                  </div>
                </div>

                {/* Voice Response feedback text */}
                <div className="space-y-1">
                  <label className="text-[8px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">NexoraX Synthesizer Speech Response</label>
                  <input
                    id="vcm-new-macro-response"
                    type="text"
                    placeholder="e.g. Overriding systems core parameters. Security standard updated."
                    value={newVoiceResponse}
                    onChange={(e) => setNewVoiceResponse(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] placeholder-zinc-700 outline-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowConfigForm(false)}
                    className="px-3 py-1.5 bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 text-zinc-400 font-mono text-[9px] uppercase rounded cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    id="vcm-submit-macro-btn"
                    type="submit"
                    className="px-3.5 py-1.5 bg-[#e240f3] hover:bg-[#e240f3]/80 text-black font-mono text-[9px] font-bold uppercase rounded cursor-pointer flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    ACTUATE REGISTERED NODE
                  </button>
                </div>
              </form>
            )}

            {/* Search Bar for Quick Action Macros */}
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <Search className="h-3 w-3 text-zinc-500" />
              </span>
              <input
                id="vcm-macro-search-input"
                type="text"
                placeholder="Search custom voice macros by trigger, landing sub-tab, or payload..."
                value={macroSearchQuery}
                onChange={(e) => setMacroSearchQuery(e.target.value)}
                className="w-full pl-8 pr-12 py-1.5 bg-zinc-950 border border-zinc-900 rounded text-[11px] font-mono text-zinc-300 placeholder-zinc-700 focus:outline-none focus:border-[#e240f3] focus:ring-1 focus:ring-[#e240f3]/25 transition-all outline-none"
              />
              {macroSearchQuery && (
                <button
                  type="button"
                  onClick={() => setMacroSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-zinc-500 hover:text-zinc-300 text-[8px] font-mono uppercase tracking-wider cursor-pointer font-bold"
                >
                  Clear
                </button>
              )}
            </div>

            {/* List of Custom Quick Action Macros */}
            <div className="space-y-2">
              {quickActions.length === 0 ? (
                <div className="text-center py-4 bg-zinc-900/10 border border-dashed border-zinc-800/80 rounded font-mono text-[9px] text-zinc-600">
                  NO CUSTOM INTENT QUICK ACTIONS BOUND. CLICK 'CREATE MACRO' TO ASSIGN PHRASES.
                </div>
              ) : (() => {
                const filtered = quickActions.filter((qa) => {
                  const query = macroSearchQuery.toLowerCase().trim();
                  if (!query) return true;
                  return (
                    qa.customPhrase.toLowerCase().includes(query) ||
                    qa.targetTab.toLowerCase().includes(query) ||
                    (qa.landingTab && qa.landingTab.toLowerCase().includes(query)) ||
                    qa.macroAction.toLowerCase().includes(query) ||
                    qa.voiceResponse.toLowerCase().includes(query)
                  );
                });

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-6 bg-zinc-900/10 border border-dashed border-zinc-800/80 rounded font-mono text-[9px] text-zinc-500 uppercase">
                      No voice macros match search query: "{macroSearchQuery}"
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {filtered.map((qa) => (
                      <div
                        key={qa.id}
                        id={`vcm-macro-item-${qa.id}`}
                        className="p-3 bg-zinc-950/70 border border-zinc-900/80 hover:border-[#e240f3]/20 rounded transition-all flex flex-col justify-between gap-2.5 group"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-mono text-zinc-200 font-bold uppercase group-hover:text-[#e240f3] transition-colors truncate max-w-[120px]">
                              "{qa.customPhrase}"
                            </span>
                            <span className="text-[8px] font-mono px-1 py-0.2 bg-purple-950/40 text-[#e240f3] border border-[#e240f3]/10 rounded">
                              Custom Trigger
                            </span>
                          </div>

                          <div className="text-[9px] font-mono space-y-0.5 text-zinc-500">
                            <div>Nav: <span className="text-indigo-400 font-semibold">{qa.targetTab}</span>{qa.landingTab ? ` > ${qa.landingTab}` : ""}</div>
                            {qa.macroAction !== "NONE" && (
                              <div className="flex items-center gap-1">
                                <span className="text-zinc-650">Payload:</span>
                                <span className="bg-amber-950/30 text-amber-500 text-[8px] px-1 rounded border border-amber-500/10 font-bold">{qa.macroAction}</span>
                              </div>
                            )}
                          </div>
                          <p className="text-[9px] text-zinc-400 italic truncate" title={qa.voiceResponse}>
                            "{qa.voiceResponse}"
                          </p>

                          {/* Render the Sequence Steps list beautifully */}
                          {qa.sequenceSteps && qa.sequenceSteps.length > 0 && (
                            <div className="text-[8px] font-mono text-zinc-500 border-t border-zinc-900/40 pt-1.5 mt-1">
                              <span className="text-[#e240f3] font-bold uppercase block mb-1">Sequence Steps ({qa.sequenceSteps.length}):</span>
                              <div className="flex flex-col gap-0.5 max-h-20 overflow-y-auto custom-scrollbar">
                                {qa.sequenceSteps.map((step, sIdx) => (
                                  <div key={step.id || sIdx} className="text-zinc-450 truncate flex items-center gap-1">
                                    <span className="text-zinc-600">{sIdx + 1}.</span>
                                    <span className="text-indigo-400 font-bold uppercase">{step.type}</span>
                                    {step.paramString && <span className="text-amber-500">"{step.paramString}"</span>}
                                    {step.paramNumber !== undefined && <span className="text-green-400">({step.paramNumber})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-1 border-t border-zinc-900/70">
                          {/* Simulate Test Trigger */}
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                setTranscript(qa.customPhrase);
                                processRawIntent(qa.customPhrase);
                              }}
                              className="px-2 py-0.5 bg-indigo-950/20 hover:bg-indigo-600 hover:text-white border border-indigo-500/10 rounded font-mono text-[8.5px] text-indigo-400 transition-colors uppercase cursor-pointer"
                            >
                              Trigger
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditorModal(qa)}
                              className="px-2 py-0.5 bg-emerald-950/20 hover:bg-emerald-600 hover:text-white border border-emerald-500/20 rounded font-mono text-[8.5px] text-emerald-400 transition-colors uppercase cursor-pointer flex items-center gap-0.5"
                              title="Edit Sequence"
                            >
                              <Edit className="w-2.5 h-2.5" />
                              Edit Sequence
                            </button>
                          </div>

                          {/* Wreak / Delete */}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = quickActions.filter((item) => item.id !== qa.id);
                              saveQuickActionsList(updated);
                              addLog(`QUICK ACTION DELETED: [Trigger: "${qa.customPhrase}"] removed from configurations.`);
                              speakText("Custom gesture macro deleted.");
                            }}
                            className="p-1 text-zinc-500 hover:text-rose-500 transition-colors cursor-pointer"
                            title="Delete Macro"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            </div>
          </div>

          {/* Category Filter Selector Tabs */}
          <div id="vcm-categories-filter" className="flex items-center gap-1.5 overflow-x-auto pb-1.5">
            {categories.map((cat, i) => (
              <button
                key={i}
                id={`vcm-cat-button-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === cat 
                    ? "bg-indigo-500 text-black font-black" 
                    : "bg-zinc-900/60 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Structured Voice Action list */}
          <div id="vcm-commands-directory" className="space-y-2 max-h-[460px] overflow-y-auto pr-1.5 custom-scrollbar">
            <AnimatePresence mode="wait">
              {filteredCommands.map((cmd) => (
                <motion.div
                  key={cmd.id}
                  id={`vcm-intent-item-${cmd.id}`}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="p-3.5 bg-zinc-950/80 border border-zinc-900 hover:border-indigo-500/30 rounded-lg group transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div className="space-y-1.5 max-w-md">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-mono font-bold text-white uppercase tracking-wider group-hover:text-indigo-400 transition-colors">
                        "{cmd.actionName}"
                      </span>
                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-500 border border-zinc-800">
                        {cmd.category}
                      </span>
                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-indigo-950/40 text-indigo-300 border border-indigo-500/10">
                        {cmd.systemAction}
                      </span>
                    </div>

                    <p className="text-[11px] text-zinc-400 font-sans leading-normal">
                      {cmd.description}
                    </p>

                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 group-hover:text-amber-400/80 transition-colors">
                      <span className="text-zinc-600">Voice Spoken Intents:</span>
                      <div className="flex flex-wrap gap-1">
                        {cmd.intents.map((intent, idx) => (
                          <span key={idx} className="bg-zinc-900 px-1 rounded text-zinc-400 text-[9px]">
                            {intent}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Operational Interactive Test Trigger Button */}
                  <button
                    id={`vcm-test-trigger-${cmd.id}`}
                    type="button"
                    onClick={() => {
                      setTranscript(cmd.intents[0]);
                      processRawIntent(cmd.intents[0]);
                    }}
                    className="sm:self-center bg-indigo-900/40 hover:bg-indigo-600 border border-indigo-500/30 text-indigo-200 hover:text-white px-3 py-2 rounded font-mono text-[9px] uppercase tracking-widest font-black transition-all flex items-center justify-center gap-1 shrink-0 cursor-pointer shadow-sm group-hover:shadow-[0_0_10px_rgba(99,102,241,0.2)]"
                  >
                    <Play className="w-3 h-3 text-indigo-400 group-hover:text-white" />
                    TEST TRIGGER
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            {filteredCommands.length === 0 && (
              <div className="text-center py-8 text-zinc-600 font-mono text-xs">
                NODE SPECTRA: NO REGISTERED INTENTS FOUND MATCHING SELECTION.
              </div>
            )}
          </div>

          {/* Quick Access Corporate Note Info footer */}
          <div id="vcm-intel-banner" className="p-3 bg-[#0a1420]/80 border border-[#00f0ff]/20 rounded-lg text-slate-300 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-[#00f0ff]" />
              <span className="text-[9px] font-mono text-[#00f0ff] uppercase tracking-widest font-bold">NEXORAX CORPORATE INTEGRITY PROMPT</span>
            </div>
            <p className="text-[10px] font-sans text-slate-400 leading-normal">
              Voice interactions are linked immediately with Node 16 (The Gavel) for active reputation audits and Secure Payment Provider (Sort Code: <strong>04-**-**</strong> | Act: <strong>******15</strong>) 1% Perpetual 3% Community Allocation distributions.
            </p>
          </div>

        </div>

      </div>

      {/* NexoraX Built-in Macro Editor Modal */}
      <AnimatePresence>
        {showEditorModal && editingMacro && (
          <div 
            id="vcm-sequence-modal-container" 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              id="vcm-seq-modal-card"
              className="w-full max-w-4xl bg-zinc-950 border-2 border-[#e240f3] rounded-xl shadow-[0_0_50px_rgba(226,64,243,0.25)] flex flex-col max-h-[90vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-zinc-900 bg-zinc-900/40 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 px-1.5 bg-[#e240f3]/10 text-[#e240f3] rounded border border-[#e240f3]/20 font-mono text-[9px] uppercase font-black animate-pulse">ADMIN LEVEL</span>
                  <div>
                    <h3 className="text-sm font-bold font-mono tracking-wide text-zinc-100 uppercase">NexoraX Macro Sequence Architect</h3>
                    <p className="text-[10px] text-zinc-500 font-mono">NODE 17: CHRONOS LEDGER / SEQUENCED INSTRUCTION BUFFER</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditorModal(false);
                    setEditingMacro(null);
                  }}
                  className="p-1.5 hover:bg-zinc-900 rounded text-zinc-400 hover:text-zinc-100 transition-colors uppercase font-mono text-[9px] font-bold cursor-pointer"
                >
                  Close
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 overflow-y-auto space-y-4 custom-scrollbar text-zinc-300 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Spoken Trigger Phrase */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-mono font-bold text-[#e240f3] uppercase tracking-wider block">Spoken Intent Trigger Phrase</label>
                    <input
                      id="modal-trigger-phrase"
                      type="text"
                      required
                      placeholder="e.g. initialize override matrix"
                      value={modalPhrase}
                      onChange={(e) => setModalPhrase(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] placeholder-zinc-700 outline-none"
                    />
                    <span className="text-[8px] text-zinc-500 font-mono uppercase block">Must match the exact spoken key to actuate.</span>
                  </div>

                  {/* Speech Response */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-mono font-bold text-zinc-400 uppercase tracking-wider block">Synthesized Speech Confirmation Feedback</label>
                    <input
                      id="modal-speech-response"
                      type="text"
                      placeholder="e.g. Complete override activated. Setting matrix level, standby."
                      value={modalResponse}
                      onChange={(e) => setModalResponse(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs font-mono text-zinc-200 focus:border-[#e240f3] placeholder-zinc-700 outline-none"
                    />
                    <span className="text-[8px] text-zinc-500 font-mono uppercase block">Audio verbal feedback synthesized back to the operator.</span>
                  </div>
                </div>

                {/* Steps List section */}
                <div className="border border-zinc-900 rounded-lg p-4 bg-zinc-950/40 space-y-3">
                  <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
                    <div className="flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-[10px] font-mono font-bold text-zinc-200 uppercase tracking-wider">Multi-Step Execution Pipeline ({modalSteps.length})</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddModalStep}
                      className="px-2.5 py-1 bg-[#e240f3]/25 hover:bg-[#e240f3] hover:text-black border border-[#e240f3]/30 text-[#e240f3] text-[9px] font-mono font-bold rounded uppercase transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      Add Action Step
                    </button>
                  </div>

                  {modalSteps.length === 0 ? (
                    <div className="text-center py-6 text-zinc-650 font-mono text-[9px] uppercase border border-dashed border-zinc-905 rounded">
                      No sequence steps mapped yet. Click "Add Action Step" above.
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[30vh] overflow-y-auto pr-1">
                      {modalSteps.map((step, idx) => (
                        <div 
                          key={step.id || idx}
                          id={`modal-step-item-${step.id}`}
                          className="p-3 bg-zinc-950 border border-zinc-900 hover:border-zinc-800 rounded-lg flex flex-col md:flex-row gap-3 items-start md:items-center justify-between transition-all"
                        >
                          {/* Left: Move Controls + Index */}
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => handleMoveStep(idx, "UP")}
                                className="p-0.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 disabled:opacity-30 rounded text-[7px] font-bold font-mono cursor-pointer"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                disabled={idx === modalSteps.length - 1}
                                onClick={() => handleMoveStep(idx, "DOWN")}
                                className="p-0.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 disabled:opacity-30 rounded text-[7px] font-bold font-mono cursor-pointer"
                              >
                                ▼
                              </button>
                            </div>
                            <span className="text-[10px] font-mono font-black text-[#e240f3] bg-[#e240f3]/5 px-2 py-1 rounded border border-[#e240f3]/10">
                              #{idx + 1}
                            </span>
                          </div>

                          {/* Middle: Action Type Selector */}
                          <div className="flex-1 space-y-1">
                            <label className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">Action Type</label>
                            <select
                              value={step.type}
                              onChange={(e) => handleUpdateStepType(step.id, e.target.value as any)}
                              className="w-full bg-zinc-950 border border-zinc-850 rounded px-2 py-1 text-[11px] font-mono text-indigo-400 focus:border-[#e240f3] outline-none"
                            >
                              <option value="OPEN_TAB">OPEN_TAB (Navigate to main dashboard + landing sub-tab)</option>
                              <option value="SET_OPACITY">SET_OPACITY (Dim or adjust Panel display opacity level)</option>
                              <option value="NOTIFY_SUPPORT">NOTIFY_SUPPORT (Dispatch log alert banner to terminal)</option>
                              <option value="TOGGLE_ANC_BOOST">TOGGLE_ANC_BOOST (Toggle active vocal noise attenuation)</option>
                              <option value="SIMULATE_REVENUE_INFLOW">SIMULATE_REVENUE_INFLOW (Dispatches simulated Secure Payment Provider Pulse)</option>
                              <option value="TRIGGER_GAVEL_ALARM">TRIGGER_GAVEL_ALARM (Activate instant Gavel stamp guard alarm)</option>
                              <option value="CALCULATE_NEXORAX_TAX">CALCULATE_NEXORAX_TAX (Execute real-time balance metrics audit)</option>
                            </select>
                          </div>

                          {/* Right side params: context dependent inputs */}
                          <div className="flex-[1.5] w-full grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
                            {/* Option 1: Open Tab inputs */}
                            {step.type === "OPEN_TAB" && (
                              <>
                                <div className="space-y-0.5">
                                  <label className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">Main Target Tab</label>
                                  <select
                                    value={step.paramString || "NODE 0: ARCHITECT TERMINAL"}
                                    onChange={(e) => handleUpdateStepString(step.id, e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-850 rounded px-1.5 py-1 text-[10px] font-mono text-zinc-200 outline-none"
                                  >
                                    <option value="NODE 0: ARCHITECT TERMINAL">NODE 0: ARCHITECT TERMINAL</option>
                                    <option value="REALITY AUDIT LEDGER">REALITY AUDIT LEDGER</option>
                                    <option value="NEXORAX WORKPLACE">NEXORAX WORKPLACE</option>
                                  </select>
                                </div>
                                <div className="space-y-0.5">
                                  <label className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">Landing Tab URL</label>
                                  <input
                                    type="text"
                                    placeholder="e.g. Home, Login, Join"
                                    value={step.landingTabUrl || ""}
                                    onChange={(e) => handleUpdateStepLanding(step.id, e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-850 rounded px-1.5 py-1 text-[10px] font-mono text-zinc-200 placeholder-zinc-800 outline-none"
                                  />
                                </div>
                              </>
                            )}

                            {/* Option 2: Opacity Input setting slider/multiplier */}
                            {step.type === "SET_OPACITY" && (
                              <div className="sm:col-span-2 space-y-0.5">
                                <label className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">
                                  Opacity Ratio: <span className="text-[#e240f3] font-bold">{(step.paramNumber !== undefined ? step.paramNumber : 1.0) * 105}%</span>
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="range"
                                    min="0.1"
                                    max="1.0"
                                    step="0.05"
                                    value={step.paramNumber !== undefined ? step.paramNumber : 1.0}
                                    onChange={(e) => handleUpdateStepNumber(step.id, parseFloat(e.target.value))}
                                    className="w-full accent-[#e240f3] cursor-pointer"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Option 3: Support notification message */}
                            {step.type === "NOTIFY_SUPPORT" && (
                              <div className="sm:col-span-2 space-y-0.5">
                                <label className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">Custom support message log payload</label>
                                <input
                                  type="text"
                                  placeholder="e.g. WARNING: core memory warning triggered by admin."
                                  value={step.paramString || ""}
                                  onChange={(e) => handleUpdateStepString(step.id, e.target.value)}
                                  className="w-full bg-zinc-950 border border-zinc-850 rounded px-1.5 py-1 text-[10px] font-mono text-zinc-200 placeholder-zinc-800 outline-none"
                                />
                              </div>
                            )}

                            {/* Option 4: Empty feedback parameters if static system actions */}
                            {["TOGGLE_ANC_BOOST", "SIMULATE_REVENUE_INFLOW", "TRIGGER_GAVEL_ALARM", "CALCULATE_NEXORAX_TAX"].includes(step.type) && (
                              <div className="sm:col-span-2 flex items-center justify-center p-2 rounded bg-zinc-900/30 text-[9px] font-mono text-zinc-600 uppercase text-center w-full">
                                Native macro protocol (no variables required).
                              </div>
                            )}
                          </div>

                          {/* Delete Action Step Button */}
                          <button
                            type="button"
                            onClick={() => handleDeleteModalStep(step.id)}
                            className="p-1 px-2 text-[9px] text-zinc-500 hover:text-rose-400 hover:bg-rose-950/20 border border-transparent hover:border-rose-950 rounded font-mono transition-colors uppercase self-center cursor-pointer"
                            title="Remove Step"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer Controls */}
              <div className="p-4 border-t border-zinc-900 bg-zinc-950/80 flex items-center justify-between">
                <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-wider block">
                  ⚙️ INTEGRITY CHECKS ACTIVE. © NEXORAX SYSTEM MATRIX V2
                </span>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditorModal(false);
                      setEditingMacro(null);
                    }}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 text-zinc-400 font-mono text-[10px] uppercase font-bold rounded cursor-pointer transition-colors"
                  >
                    Discard Changes
                  </button>
                  <button
                    id="save-macro-sequence-btn"
                    type="button"
                    onClick={handleSaveSequenceMacro}
                    className="px-4.5 py-2 bg-[#e240f3] hover:bg-[#e240f3]/90 text-black font-mono text-[10px] uppercase font-bold rounded cursor-pointer transition-all flex items-center gap-1.5 shadow-[0_0_15px_rgba(226,64,243,0.3)]"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-black" />
                    CONFIRM & LOCK SEQUENCE
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};
